import type { JobTypeInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { createJobType, deactivateJobType, listJobTypes, reactivateJobType, updateJobType, type JobType } from '../../../reference-data.api';

export type JobTypesState = { status: 'loading' } | { status: 'loaded'; types: JobType[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): JobTypesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** Same shape as useTrailerTypes: create/update keep their error inline, deactivate/reactivate toast. */
export function useJobTypes() {
  const [state, setState] = useState<JobTypesState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listJobTypes()
      .then((types) => {
        if (!cancelled) setState({ status: 'loaded', types });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', types: await listJobTypes() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.status === 409 ? 'A job type with this code already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: JobTypeInput) => save(() => createJobType(input), `${input.name} created`);
  const update = (id: string, input: JobTypeInput) => save(() => updateJobType(id, input), `${input.name} updated`);
  const deactivate = (row: JobType) => rowAction(() => deactivateJobType(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: JobType) => rowAction(() => reactivateJobType(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
